"use client";
import React from 'react';
import { useTranslations } from 'next-intl';
import styles from './StyledComponents.module.css';

interface PriceDisplayProps {
  loadingPrice: boolean;
  price: { price: number; discount: number; price_no_discount: number } | undefined;
  selectedValue: number | null;
  validationMsg: string | null;
  savings: number;
  priceDisplay: {
    price: number;
    priceNoDiscount: number;
    savings: number;
    faceValue: number;
    faceValueCurrency: string;
    fee: number;
    currency: string;
    isConverted: boolean;
  } | null;
}

export const PriceDisplay = React.memo<PriceDisplayProps>(({
  loadingPrice,
  price,
  selectedValue,
  validationMsg,
  savings,
  priceDisplay,
}) => {
  const t = useTranslations('product');

  if (loadingPrice) {
    return (
      <div className={styles.priceLoadingIndicator}>
        <div className={styles.loadingFlex}>
          <span style={{ width: 20, height: 20, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite', display: 'inline-block' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
          <span>{t('pricing.calculating')}</span>
        </div>
      </div>
    );
  }

  if (!price || !selectedValue || validationMsg || !priceDisplay) return null;

  const hasFee = priceDisplay.fee > 0.001;
  const hasSavings = savings > 0 && priceDisplay.savings > 0;
  const discountPct = hasSavings
    ? (priceDisplay.savings / (priceDisplay.price + priceDisplay.savings)) * 100
    : 0;

  return (
    <div className={styles.priceSummaryCard}>
      <div className={styles.priceSummaryRow}>
        <div className={styles.priceSummaryCol}>
          <div className={styles.priceSummaryLabel}>{t('pricing.giftCardValue')}</div>
          <div className={styles.priceSummaryValue}>
            {priceDisplay.faceValueCurrency} {priceDisplay.faceValue.toFixed(2)}
          </div>
        </div>
        <div className={`${styles.priceSummaryCol} ${styles.priceSummaryColEnd}`}>
          <div className={styles.priceSummaryLabel}>{t('pricing.youPay')}</div>
          <div className={`${styles.priceSummaryValue} ${styles.priceSummaryValuePrimary} ${hasSavings ? styles.priceSummaryValueSaving : ''}`}>
            {priceDisplay.currency} {priceDisplay.price.toFixed(2)}
          </div>
        </div>
      </div>

      {/* don't display fking fee */}
      {/* {hasFee && (
        <div className={styles.priceSummaryFootnote}>
          <span>{t('pricing.includesFee', { amount: `${priceDisplay.currency} ${priceDisplay.fee.toFixed(2)}` })}</span>
        </div>
      )} */}

      {hasSavings && (
        <div className={`${styles.priceSummaryFootnote} ${styles.priceSummaryFootnoteSaving}`}>
          <span>{`${t('pricing.youSave')} ${priceDisplay.currency} ${priceDisplay.savings.toFixed(2)} (${discountPct.toFixed(2)}%)`}</span>
        </div>
      )}
    </div>
  );
});

PriceDisplay.displayName = 'PriceDisplay';
